import React, { useMemo, useState } from 'react';
import cn from 'classnames';
import isEqual from 'lodash/isEqual';
import isBoolean from 'lodash/isBoolean';
import FinalForm, { FieldArray, FormSpy } from '@framework/ui/Organisms/FinalForm';
import { TFinalFormRenderProp } from '@framework/ui/Organisms/FinalForm/interfaces';
import { useFormPayloadContext } from '@/creditflowresearch/application/PortfolioMonitor/FormPayloadContext';
import {
  CUSIPS_LIST_ARRAY_NAME,
  getInitialListsValues,
} from './constants';
import CusipsList from './CusipsList';
import { useStepFormContext } from '@/creditflowresearch/application/PortfolioMonitor/StepFormContext';
import { usePortfoliosContext } from '@/creditflowresearch/application/PortfolioMonitor/PortfoliosContext';
import FormBottomNavigation
  from '@/creditflowresearch/ui/components/portfolioMonitor/PortfolioContent/common/FormBottomNavigation';
import { ICusipErrors, validate, validateCusips } from './validators';
import {
  getPositionsFromFieldValues,
  transformErrorDataToValidateCusipsData,
  getValidatedLists,
  updatePayloadFromCusipsResponse,
  updatePayloadFromTranchesResponse,
} from './helpers';
import { ICreateListsFields } from '@/creditflowresearch/domain/vo/PortfolioMonitor/ICreatePortfolioFormState';

import cardStyles from '@creditflowresearch/uilib/styles/card.module.scss';
import spaceStyles from '@creditflowresearch/uilib/styles/helpers/spaces.module.scss';
import formStyles from '@/creditflowresearch/ui/components/portfolioMonitor/PortfolioContent/PortfolioForm/formStyles.module.scss';
import styles from './createLists.module.scss';

interface IProps {
  setIsAnyChanges: React.Dispatch<React.SetStateAction<boolean>>,
  cancelButtonClick: () => void,
}

const CreateListsForm = ({ setIsAnyChanges, cancelButtonClick }: IProps) => {
  const { isLoading, getBondsByCusips, getBondsByTrancheIds, selectedPortfolio } = usePortfoliosContext();
  const { goNext } = useStepFormContext();
  const { setPayload, payload } = useFormPayloadContext();
  const initialValues = useMemo(() => (
    { [CUSIPS_LIST_ARRAY_NAME]: getInitialListsValues(selectedPortfolio?.id, payload?.lists) }
  ), [selectedPortfolio?.id, payload?.lists]);
  const [isCusipsValidated, setIsCusipsValidated] = useState(false);

  const updatePayloadFromFieldValues = (values: ICreateListsFields) => {
    setPayload((prevState) => ({
      ...prevState,
      lists: getPositionsFromFieldValues(values),
    }));
  };

  const getNoMappedTranchesCusipIds = (values: ICreateListsFields) => values.cusipLists.flatMap((list) => list.positions
      .filter((position) => !position?.bond?.trancheId)
      .map((position) => position.value || position.cusip),
    );

  const getMappedTrancheIds = (values) => values.cusipLists.flatMap(
      (list) => list.positions.reduce(
    (acc, { bond, isBondAutoSelected }) => {
      if (isBoolean(isBondAutoSelected)) {
        return (bond?.trancheId && isBondAutoSelected ? [...acc, bond.trancheId] : acc);
      }
      return (bond?.trancheId ? [...acc, bond.trancheId] : acc);
    },
    []),
  );

  const setErrorsForCusips = (values: ICreateListsFields, errorData: ICusipErrors) => {
    setPayload((prevState) => ({
      ...prevState,
      lists: getValidatedLists(values, errorData),
    }));
  };

  const checkUnknownCusips = async (values: ICreateListsFields) => {
    const includedCusips = getNoMappedTranchesCusipIds(values);
    if (includedCusips.length) {
      const response = await getBondsByCusips({ filter: { includedCusips } });
      if (response?.error && response?.error?.data) {
        const { data } = response.error;
        const validateData = transformErrorDataToValidateCusipsData(data);
        setErrorsForCusips(values, data);
        return validateCusips(validateData);
      }

      updatePayloadFromCusipsResponse(response, setPayload);
    }
    return null;
  };

  const checkMappedCusips = async (values: ICreateListsFields) => {
    const includedTrancheIds = getMappedTrancheIds(values);
    if (includedTrancheIds.length) {
      const responseByTrancheIds = await getBondsByTrancheIds({ filter: { includedTrancheIds } });
      updatePayloadFromTranchesResponse(responseByTrancheIds, setPayload);
    }
  };

  const removeInvalidCusips = (values: ICreateListsFields) => {
    values.cusipLists.forEach((list) => {
      list.positions = list.positions.filter((position) => position.isValid);
    });
  };

  const submit = async (values: ICreateListsFields) => {
    if (isCusipsValidated) {
      removeInvalidCusips(values);
    }

    updatePayloadFromFieldValues(values);

    const errors = await checkUnknownCusips(values);
    setIsCusipsValidated(true);
    if (errors) {
      return errors;
    }

    await checkMappedCusips(values);
    setIsCusipsValidated(false);

    scrollToTop();

    return goNext();
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const renderForm: TFinalFormRenderProp = ({
    hasValidationErrors,
    hasSubmitErrors,
    dirtySinceLastSubmit,
    values,
  }) => {
    const isSubmitDisabled = isLoading ||
      hasValidationErrors ||
      (hasSubmitErrors && !dirtySinceLastSubmit) ||
      !values[CUSIPS_LIST_ARRAY_NAME]?.length;

    return (
      <>
        <FormSpy
          onChange={
            ({ dirty, values, initialValues }) => setIsAnyChanges(
                (prev) => prev || (dirty && !isEqual(initialValues, values)),
            )
          }
        />
        <div className={ cn(formStyles.formWrapper, styles.fieldsWrapper) }>
          <FieldArray name={ CUSIPS_LIST_ARRAY_NAME } component={ CusipsList } />
        </div>
        <FormBottomNavigation
          isLoading={ isLoading }
          isSubmitDisabled={ isSubmitDisabled }
          caption="Lists are collections of securities that are logically grouped."
          onCancelClick={ cancelButtonClick }
        />
      </>
    );
  };

  return (
    <div className={ cn(cardStyles.cardContainer, spaceStyles.mbn) }>
      <h3 className={ cn(spaceStyles.mll, spaceStyles.mrl, spaceStyles.pbl, spaceStyles.ptl) }>Create Lists</h3>
      <FinalForm
        dataTest="portfolioMonitorCreateListForm"
        render={ renderForm }
        initialValues={ initialValues }
        onSubmit={ submit }
        validate={ validate }
      />
    </div>
  );
};

export default CreateListsForm;
